import React, { useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import {
  TrendingUp,
  User,
  Users,
  Brain,
  Globe,
  FileText,
  Mail,
  BarChart3,
  Eye,
  Layout,
  Shield,
  MessageSquare,
  ChevronDown,
  ChevronUp,
  Zap,
  Target,
  Star,
  Languages,
  Code2,
  Server,
  Bot,
  DollarSign,
  Calculator,
  UserPlus,
  Building2,
  ArrowRight,
  AlertTriangle,
  CheckCircle2,
  TrendingDown,
} from 'lucide-react';

// ============================================================================
// Modèle d'estimation : temps mensuel par activité pour Stratos Premium
// ============================================================================

type DifficultyLevel = 'junior' | 'confirmed' | 'senior' | 'expert';

interface TimeActivity {
  id: string;
  category: string;
  label: string;
  description: string;
  icon: React.ElementType;
  monthlyHoursMin: number;
  monthlyHoursMax: number;
  frequency: string;          // "quotidien", "hebdo", "mensuel", "one-shot"
  isOnboarding: boolean;      // True = uniquement le premier mois
  details: string[];          // Sous-tâches détaillées
  expertise: 'technical' | 'strategic' | 'creative' | 'operational';
  automatable: number;        // 0-100% d'automatisation possible à terme
  difficulty: DifficultyLevel; // Niveau requis pour cette tâche
  canDelegate: boolean;       // Peut être externalisé/délégué à un junior/freelance
}

// Grille de coûts horaires par niveau (coût interne chargé ou freelance)
const HOURLY_RATES: Record<DifficultyLevel, { label: string; rate: number; color: string; bg: string }> = {
  junior:    { label: 'Junior',    rate: 25,  color: 'text-green-600',  bg: 'bg-green-50' },
  confirmed: { label: 'Confirmé',  rate: 40,  color: 'text-blue-600',   bg: 'bg-blue-50' },
  senior:    { label: 'Senior',    rate: 60,  color: 'text-purple-600', bg: 'bg-purple-50' },
  expert:    { label: 'Expert',    rate: 85,  color: 'text-red-600',    bg: 'bg-red-50' },
};

const ACTIVITIES: TimeActivity[] = [
  // === ONBOARDING (Mois 1 uniquement) ===
  {
    id: 'nda_contract',
    category: 'Onboarding',
    label: 'NDA & Contrat',
    description: 'Génération et personnalisation du NDA, contrat d\'accompagnement, devis, CGV',
    icon: Shield,
    monthlyHoursMin: 1.5,
    monthlyHoursMax: 3,
    frequency: 'one-shot',
    isOnboarding: true,
    details: [
      'Personnalisation du NDA avec données client',
      'Rédaction du contrat sur-mesure (pack, durée, tarif)',
      'Génération du devis détaillé',
      'Envoi et suivi des signatures',
    ],
    expertise: 'operational',
    automatable: 70,
    difficulty: 'junior',
    canDelegate: true,
  },
  {
    id: 'initial_audit',
    category: 'Onboarding',
    label: 'Audit complet initial',
    description: 'Audit SEO technique, sémantique, local + analyse concurrentielle + état des lieux GEO',
    icon: BarChart3,
    monthlyHoursMin: 8,
    monthlyHoursMax: 14,
    frequency: 'one-shot',
    isOnboarding: true,
    details: [
      'Crawl technique du site (indexation, vitesse, erreurs)',
      'Analyse sémantique (mots-clés, intentions, contenus)',
      'Audit SEO local (fiche Google, citations, avis)',
      'Benchmark concurrentiel (3-5 concurrents)',
      'Cartographie GEO : test des requêtes sur ChatGPT, Mistral, Perplexity, Gemini',
      'Rédaction du rapport de pré-audit professionnel',
    ],
    expertise: 'technical',
    automatable: 30,
    difficulty: 'senior',
    canDelegate: false,
  },
  {
    id: 'action_plan',
    category: 'Onboarding',
    label: 'Plan d\'action stratégique',
    description: 'Définition des priorités, roadmap 6 mois, objectifs mesurables',
    icon: Target,
    monthlyHoursMin: 3,
    monthlyHoursMax: 5,
    frequency: 'one-shot',
    isOnboarding: true,
    details: [
      'Priorisation des actions (matrice ICE)',
      'Roadmap mois par mois sur 6 mois',
      'Définition des KPIs de suivi',
      'Présentation au client',
    ],
    expertise: 'technical',
    automatable: 20,
    difficulty: 'senior',
    canDelegate: false,
  },

  // === RECURRENT (Chaque mois) ===
  {
    id: 'website_geo',
    category: 'SEO & GEO',
    label: 'Optimisation site web GEO',
    description: 'Création de FAQ, pages d\'intention, landing pages optimisées pour les IA',
    icon: Globe,
    monthlyHoursMin: 5,
    monthlyHoursMax: 9,
    frequency: 'mensuel',
    isOnboarding: false,
    details: [
      'Recherche des questions que posent les IA pour constituer leurs listes',
      'Création/optimisation de FAQ structurées (schema.org)',
      'Rédaction de pages d\'intention internaute (ex: "restaurant pour déjeuner d\'affaires invalides")',
      'Optimisation des balises title, meta, H1, données structurées',
      'Création de landing pages ciblées par quartier/occasion',
    ],
    expertise: 'technical',
    automatable: 25,
    difficulty: 'confirmed',
    canDelegate: false,
  },
  {
    id: 'ai_prompt_sim',
    category: 'SEO & GEO',
    label: 'Simulations de prompt IA',
    description: 'Reverse-engineering des logiques de ranking IA : test des requêtes sur ChatGPT, Mistral, Perplexity, Gemini',
    icon: Brain,
    monthlyHoursMin: 2,
    monthlyHoursMax: 4,
    frequency: 'hebdo',
    isOnboarding: false,
    details: [
      'Identification des requêtes types que les IA utilisent pour consolider leurs listes',
      'Test systématique sur 4-6 plateformes IA (prompts, variantes, contexte)',
      'Analyse des critères de citation (avis, contenu structuré, localisation, autorité)',
      'Ajustement technique du contenu et des données structurées en conséquence',
      'Documentation des positions et variations par plateforme',
    ],
    expertise: 'technical',
    automatable: 40,
    difficulty: 'expert',
    canDelegate: false,
  },
  {
    id: 'gbp_management',
    category: 'Google Business',
    label: 'Animation fiche Google',
    description: 'Ajout photos, articles, posts, réponse aux avis, optimisations régulières',
    icon: Star,
    monthlyHoursMin: 3,
    monthlyHoursMax: 5,
    frequency: 'hebdo',
    isOnboarding: false,
    details: [
      'Publication de 4-6 Google Posts par mois',
      'Ajout de photos professionnelles (plats, ambiance)',
      'Réponse aux avis Google (positifs et négatifs)',
      'Mise à jour des informations (horaires, menus, événements)',
      'Suivi des statistiques GBP (vues, actions, appels)',
    ],
    expertise: 'creative',
    automatable: 35,
    difficulty: 'junior',
    canDelegate: true,
  },
  {
    id: 'review_campaign',
    category: 'Google Business',
    label: 'Campagne d\'avis Google',
    description: 'Export BDD clients, envoi de 20 mails/jour, automation tracking, suivi des retours',
    icon: Mail,
    monthlyHoursMin: 2,
    monthlyHoursMax: 3,
    frequency: 'quotidien',
    isOnboarding: false,
    details: [
      'Export et nettoyage de la base clients du restaurant',
      'Configuration de l\'automation d\'envoi (20 mails/jour)',
      'Rédaction des templates d\'email personnalisés',
      'Suivi des taux d\'ouverture et de conversion',
      'Relances ciblées et ajustements',
    ],
    expertise: 'operational',
    automatable: 60,
    difficulty: 'junior',
    canDelegate: true,
  },
  {
    id: 'landing_pages',
    category: 'SEO & GEO',
    label: 'Création de landing pages',
    description: 'Pages ciblées par quartier, occasion, type de cuisine pour capter le trafic longue traîne',
    icon: Layout,
    monthlyHoursMin: 3,
    monthlyHoursMax: 6,
    frequency: 'mensuel',
    isOnboarding: false,
    details: [
      'Recherche des opportunités de mots-clés longue traîne',
      'Rédaction de contenus optimisés (800-1500 mots)',
      'Intégration sur le site (HTML/CMS)',
      'Optimisation technique (vitesse, schema.org, internal linking)',
    ],
    expertise: 'technical',
    automatable: 30,
    difficulty: 'confirmed',
    canDelegate: true,
  },
  {
    id: 'translations',
    category: 'SEO & GEO',
    label: 'Traduction multilingue',
    description: 'Traduction et adaptation des contenus créés en 3-4 langues (EN, ES, PT, JP selon le client)',
    icon: Languages,
    monthlyHoursMin: 1.5,
    monthlyHoursMax: 3,
    frequency: 'mensuel',
    isOnboarding: false,
    details: [
      'Traduction des nouvelles landing pages en 3-4 langues',
      'Adaptation SEO par langue (mots-clés locaux, intentions de recherche)',
      'Vérification des balises hreflang et du maillage multilingue',
      'Optimisation des meta descriptions par langue pour les SERP internationales',
    ],
    expertise: 'technical',
    automatable: 50,
    difficulty: 'junior',
    canDelegate: true,
  },
  {
    id: 'indexation_tracking',
    category: 'Suivi & Reporting',
    label: 'Suivi indexation & ranking',
    description: 'Monitoring Search Console, suivi des positions, détection des problèmes d\'indexation',
    icon: TrendingUp,
    monthlyHoursMin: 1.5,
    monthlyHoursMax: 3,
    frequency: 'hebdo',
    isOnboarding: false,
    details: [
      'Vérification de l\'indexation des nouvelles pages',
      'Suivi des positions sur les mots-clés cibles',
      'Analyse des performances Search Console',
      'Détection et résolution des erreurs (404, redirections)',
    ],
    expertise: 'technical',
    automatable: 50,
    difficulty: 'junior',
    canDelegate: true,
  },
  {
    id: 'ai_visibility_tracking',
    category: 'Suivi & Reporting',
    label: 'Suivi présence IA',
    description: 'Monitoring technique de la visibilité du restaurant dans les réponses des IA génératives',
    icon: Eye,
    monthlyHoursMin: 1.5,
    monthlyHoursMax: 3,
    frequency: 'hebdo',
    isOnboarding: false,
    details: [
      'Tests hebdomadaires sur les 4-6 plateformes IA',
      'Documentation des résultats et évolutions',
      'Analyse technique des facteurs de citation/exclusion',
      'Alertes et correctifs en cas de perte de visibilité',
    ],
    expertise: 'technical',
    automatable: 55,
    difficulty: 'confirmed',
    canDelegate: false,
  },
  {
    id: 'reporting',
    category: 'Suivi & Reporting',
    label: 'Reporting mensuel',
    description: 'Compilation des données, rédaction du rapport de performance, recommandations',
    icon: FileText,
    monthlyHoursMin: 2,
    monthlyHoursMax: 4,
    frequency: 'mensuel',
    isOnboarding: false,
    details: [
      'Compilation des données (Search Console, Analytics, GBP, IA)',
      'Rédaction du rapport de performance',
      'Préparation de la présentation client',
      'Recommandations pour le mois suivant',
    ],
    expertise: 'operational',
    automatable: 45,
    difficulty: 'confirmed',
    canDelegate: true,
  },
  {
    id: 'client_meeting',
    category: 'Suivi & Reporting',
    label: 'Réunion client',
    description: 'Point mensuel de suivi, présentation des résultats, ajustements',
    icon: MessageSquare,
    monthlyHoursMin: 1,
    monthlyHoursMax: 2,
    frequency: 'mensuel',
    isOnboarding: false,
    details: [
      'Préparation de l\'ordre du jour',
      'Présentation des résultats et KPIs',
      'Discussion des prochaines actions',
      'Compte-rendu et action items',
    ],
    expertise: 'operational',
    automatable: 10,
    difficulty: 'senior',
    canDelegate: false,
  },

  // === DÉVELOPPEMENT STRATOS (R&D / Produit) ===
  {
    id: 'stratos_app_maintenance',
    category: 'Développement Stratos',
    label: 'Maintenance application Stratos',
    description: 'Maintenance, évolutions et corrections de l\'application Stratos (React/TypeScript/Supabase)',
    icon: Code2,
    monthlyHoursMin: 4,
    monthlyHoursMax: 7,
    frequency: 'mensuel',
    isOnboarding: false,
    details: [
      'Corrections de bugs et mises à jour de dépendances',
      'Développement de nouvelles fonctionnalités (dashboards, rapports, suivi)',
      'Intégration et synchronisation des données Supabase',
      'Gestion de l\'infrastructure (Supabase, hébergement, CI/CD)',
    ],
    expertise: 'technical',
    automatable: 15,
    difficulty: 'expert',
    canDelegate: false,
  },
  {
    id: 'automation_app',
    category: 'Développement Stratos',
    label: 'Outils internes & automatisation',
    description: 'Développement des outils propriétaires (70% dev interne) : scraping, alertes, dashboards, campagnes',
    icon: Server,
    monthlyHoursMin: 3,
    monthlyHoursMax: 6,
    frequency: 'mensuel',
    isOnboarding: false,
    details: [
      'Développement des workflows d\'automatisation (envoi mails, scraping positions)',
      'Intégration APIs tierces (Google, SEMrush, plateformes IA)',
      'Monitoring et alertes automatiques (perte de position, nouveaux avis)',
      '70% des outils développés en interne → réduit la dépendance aux SaaS externes',
    ],
    expertise: 'technical',
    automatable: 20,
    difficulty: 'expert',
    canDelegate: false,
  },
  {
    id: 'chatbot_rag',
    category: 'Développement Stratos',
    label: 'Chatbot propriétaire RAG',
    description: 'Chatbot IA interne pour l\'analyse SEO/GEO assistée — R&D progressive',
    icon: Bot,
    monthlyHoursMin: 2,
    monthlyHoursMax: 5,
    frequency: 'mensuel',
    isOnboarding: false,
    details: [
      'Architecture RAG : indexation des données clients (Search Console, GBP, positions IA)',
      'Fine-tuning des prompts pour l\'analyse SEO/GEO automatique',
      'Développement de l\'interface conversationnelle dans Stratos',
      'R&D progressive, pas prioritaire vs. acquisition client',
    ],
    expertise: 'technical',
    automatable: 10,
    difficulty: 'expert',
    canDelegate: false,
  },
];

const EXPERTISE_COLORS = {
  technical: { label: 'Technique', color: 'text-blue-600', bg: 'bg-blue-50', border: 'border-blue-200' },
  strategic: { label: 'Stratégique', color: 'text-purple-600', bg: 'bg-purple-50', border: 'border-purple-200' },
  creative: { label: 'Créatif', color: 'text-pink-600', bg: 'bg-pink-50', border: 'border-pink-200' },
  operational: { label: 'Opérationnel', color: 'text-amber-600', bg: 'bg-amber-50', border: 'border-amber-200' },
};

// Pricing des packs Stratos (catalogue vs effectif)
// Effectif = après remise fidélité 12 mois (-15%) et crédits parrainage (-5% en moyenne)
const PACK_PRICING = {
  essentiel: { label: 'Pack Essentiel', price: 1000, effectivePrice: 850 },
  premium:   { label: 'Pack Premium',   price: 2000, effectivePrice: 1700 },
  signature: { label: 'Pack Signature', price: 3500, effectivePrice: 3000 },
  audit:     { label: 'Audit 360',      price: 4000, effectivePrice: 4000 }, // one-shot, pas de remise
};

export const TimeEstimationPage: React.FC = () => {
  const [showOnboarding, setShowOnboarding] = useState(true);
  const [expandedActivity, setExpandedActivity] = useState<string | null>(null);
  const [clientCount, setClientCount] = useState(1);
  const [selectedPack, setSelectedPack] = useState<'essentiel' | 'premium' | 'signature'>('premium');

  const recurrentActivities = ACTIVITIES.filter((a) => !a.isOnboarding);
  const onboardingActivities = ACTIVITIES.filter((a) => a.isOnboarding);

  const totals = useMemo(() => {
    const recurrentMin = recurrentActivities.reduce((s, a) => s + a.monthlyHoursMin, 0);
    const recurrentMax = recurrentActivities.reduce((s, a) => s + a.monthlyHoursMax, 0);
    const onboardingMin = onboardingActivities.reduce((s, a) => s + a.monthlyHoursMin, 0);
    const onboardingMax = onboardingActivities.reduce((s, a) => s + a.monthlyHoursMax, 0);

    const month1Min = recurrentMin + onboardingMin;
    const month1Max = recurrentMax + onboardingMax;

    // Répartition par expertise
    const byExpertise = Object.entries(EXPERTISE_COLORS).map(([key, info]) => {
      const activities = recurrentActivities.filter((a) => a.expertise === key);
      const min = activities.reduce((s, a) => s + a.monthlyHoursMin, 0);
      const max = activities.reduce((s, a) => s + a.monthlyHoursMax, 0);
      return { key, ...info, min, max, count: activities.length };
    }).filter(e => e.count > 0);

    return { recurrentMin, recurrentMax, onboardingMin, onboardingMax, month1Min, month1Max, byExpertise };
  }, []);

  // Analyse financière
  const financials = useMemo(() => {
    // Coût interne par activité (heures moyennes × taux horaire du niveau requis)
    const activityCosts = recurrentActivities.map((a) => {
      const avgHours = (a.monthlyHoursMin + a.monthlyHoursMax) / 2;
      const rate = HOURLY_RATES[a.difficulty].rate;
      const cost = avgHours * rate;
      return { ...a, avgHours, rate, cost };
    });

    // Coûts fixes non liés aux clients (R&D Stratos) — mutualisés
    const devActivities = activityCosts.filter(a => a.category === 'Développement Stratos');
    const clientActivities = activityCosts.filter(a => a.category !== 'Développement Stratos');

    const totalDevCost = devActivities.reduce((s, a) => s + a.cost, 0);
    const clientCostPerClient = clientActivities.reduce((s, a) => s + a.cost, 0);
    const devCostPerClient = clientCount > 0 ? totalDevCost / clientCount : totalDevCost;

    const totalCostPerClient = clientCostPerClient + devCostPerClient;
    const packPrice = PACK_PRICING[selectedPack].effectivePrice;
    const marginPerClient = packPrice - totalCostPerClient;
    const marginPct = packPrice > 0 ? (marginPerClient / packPrice) * 100 : 0;

    const totalRevenue = packPrice * clientCount;
    const totalCost = (clientCostPerClient * clientCount) + totalDevCost;
    const totalMargin = totalRevenue - totalCost;
    const totalMarginPct = totalRevenue > 0 ? (totalMargin / totalRevenue) * 100 : 0;

    // Répartition par difficulté
    const byDifficulty = (Object.keys(HOURLY_RATES) as DifficultyLevel[]).map((level) => {
      const acts = clientActivities.filter(a => a.difficulty === level);
      const hours = acts.reduce((s, a) => s + a.avgHours, 0);
      const cost = acts.reduce((s, a) => s + a.cost, 0);
      return { level, ...HOURLY_RATES[level], hours, cost, count: acts.length };
    }).filter(d => d.count > 0);

    // Tâches délégables
    const delegatable = clientActivities.filter(a => a.canDelegate);
    const delegatableHours = delegatable.reduce((s, a) => s + a.avgHours, 0);
    const delegatableCost = delegatable.reduce((s, a) => s + a.cost, 0);

    // Scénarios de scaling
    const scalingScenarios = [3, 5, 8, 10, 15].map((n) => {
      const revenue = packPrice * n;
      const cost = (clientCostPerClient * n) + totalDevCost;
      const margin = revenue - cost;
      const marginP = revenue > 0 ? (margin / revenue) * 100 : 0;
      const totalHoursClient = clientActivities.reduce((s, a) => s + (a.monthlyHoursMin + a.monthlyHoursMax) / 2, 0) * n;
      const totalHoursDev = devActivities.reduce((s, a) => s + (a.monthlyHoursMin + a.monthlyHoursMax) / 2, 0);
      const totalHours = totalHoursClient + totalHoursDev;
      const ftEquivalent = totalHours / 140; // 140h = temps utile/mois salarié
      return { clients: n, revenue, cost, margin, marginP, totalHours, ftEquivalent };
    });

    return {
      activityCosts,
      clientActivities,
      devActivities,
      totalDevCost,
      clientCostPerClient,
      devCostPerClient,
      totalCostPerClient,
      packPrice,
      marginPerClient,
      marginPct,
      totalRevenue,
      totalCost,
      totalMargin,
      totalMarginPct,
      byDifficulty,
      delegatable,
      delegatableHours,
      delegatableCost,
      scalingScenarios,
    };
  }, [clientCount, selectedPack]);

  const toggleExpand = (id: string) => {
    setExpandedActivity(expandedActivity === id ? null : id);
  };

  const renderActivity = (activity: TimeActivity, index: number) => {
    const isExpanded = expandedActivity === activity.id;
    const expertiseInfo = EXPERTISE_COLORS[activity.expertise];
    const difficultyInfo = HOURLY_RATES[activity.difficulty];
    const Icon = activity.icon;
    const avgHours = (activity.monthlyHoursMin + activity.monthlyHoursMax) / 2;
    const cost = avgHours * difficultyInfo.rate;

    return (
      <motion.div
        key={activity.id}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 + index * 0.03 }}
        className={`bg-white rounded-xl border border-gray-200 hover:border-gray-300 transition-all duration-200 ${isExpanded ? 'shadow-md' : ''}`}
      >
        <button
          onClick={() => toggleExpand(activity.id)}
          className="w-full p-4 flex items-center gap-4 text-left"
        >
          <div className={`w-10 h-10 rounded-lg ${expertiseInfo.bg} flex items-center justify-center flex-shrink-0`}>
            <Icon className={`w-5 h-5 ${expertiseInfo.color}`} />
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-0.5 flex-wrap">
              <h3 className="font-semibold text-gray-900 text-sm">{activity.label}</h3>
              <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${expertiseInfo.bg} ${expertiseInfo.color}`}>
                {expertiseInfo.label}
              </span>
              <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${difficultyInfo.bg} ${difficultyInfo.color}`}>
                {difficultyInfo.label} · {difficultyInfo.rate}€/h
              </span>
              {activity.canDelegate && (
                <span className="text-[10px] px-1.5 py-0.5 rounded font-medium bg-emerald-50 text-emerald-600">
                  Délégable
                </span>
              )}
            </div>
            <p className="text-xs text-gray-500 truncate">{activity.description}</p>
          </div>

          {/* Hours + Cost */}
          <div className="text-right flex-shrink-0">
            <div className="text-lg font-bold text-gray-900">
              {activity.monthlyHoursMin}-{activity.monthlyHoursMax}h
            </div>
            <div className="text-xs font-medium text-stratos-gold">
              ~{Math.round(cost)}€
            </div>
            <div className="text-[10px] text-gray-400 uppercase tracking-wide">
              {activity.frequency}
            </div>
          </div>

          {isExpanded ? (
            <ChevronUp className="w-4 h-4 text-gray-400 flex-shrink-0" />
          ) : (
            <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" />
          )}
        </button>

        {isExpanded && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="px-4 pb-4 border-t border-gray-100"
          >
            <ul className="mt-3 space-y-2">
              {activity.details.map((detail, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-gray-600">
                  <div className="w-1.5 h-1.5 rounded-full bg-stratos-gold mt-1.5 flex-shrink-0" />
                  {detail}
                </li>
              ))}
            </ul>
            <div className="mt-3 flex items-center gap-4 text-xs">
              <span className="text-gray-400">
                Potentiel d'automatisation :
              </span>
              <div className="flex items-center gap-2 flex-1">
                <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
                  <div
                    className="h-full rounded-full bg-gradient-to-r from-stratos-gold to-stratos-gold-light"
                    style={{ width: `${activity.automatable}%` }}
                  />
                </div>
                <span className="font-semibold text-gray-600">{activity.automatable}%</span>
              </div>
            </div>
          </motion.div>
        )}
      </motion.div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <h1 className="text-3xl font-bold text-gray-900">
          Estimation temps — Stratos Premium
        </h1>
        <p className="text-gray-500 mt-1">
          Temps mensuel estimé pour l'accompagnement complet d'un client
        </p>
      </motion.div>

      {/* KPI Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          {
            label: 'Mois 1 (avec onboarding)',
            value: `${totals.month1Min}-${totals.month1Max}h`,
            sub: `${Math.round(totals.month1Min / 7.5)}-${Math.round(totals.month1Max / 7.5)} jours`,
            color: 'border-amber-300 bg-amber-50/50',
          },
          {
            label: 'Récurrent mensuel',
            value: `${totals.recurrentMin}-${totals.recurrentMax}h`,
            sub: `${Math.round(totals.recurrentMin / 7.5)}-${Math.round(totals.recurrentMax / 7.5)} jours`,
            color: 'border-emerald-300 bg-emerald-50/50',
          },
          {
            label: `Pour ${clientCount} client${clientCount > 1 ? 's' : ''}/mois`,
            value: `${totals.recurrentMin * clientCount}-${totals.recurrentMax * clientCount}h`,
            sub: `${Math.round((totals.recurrentMin * clientCount) / 7.5)}-${Math.round((totals.recurrentMax * clientCount) / 7.5)} jours`,
            color: 'border-blue-300 bg-blue-50/50',
          },
          {
            label: 'Expertise technique',
            value: `${Math.round((totals.byExpertise.find(e => e.key === 'technical')?.min || 0) / totals.recurrentMin * 100)}%`,
            sub: 'du temps récurrent',
            color: 'border-purple-300 bg-purple-50/50',
          },
        ].map((kpi, i) => (
          <motion.div
            key={kpi.label}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 + i * 0.05 }}
            className={`rounded-xl border-2 ${kpi.color} p-4`}
          >
            <div className="text-2xl font-bold text-gray-900">{kpi.value}</div>
            <div className="text-xs text-gray-500 mt-1">{kpi.label}</div>
            <div className="text-[10px] text-gray-400 mt-0.5">{kpi.sub}</div>
          </motion.div>
        ))}
      </div>

      {/* Client count selector */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.3 }}
        className="flex items-center gap-3 bg-gray-50 rounded-xl p-4"
      >
        <User className="w-5 h-5 text-gray-400" />
        <span className="text-sm text-gray-600">Nombre de clients simultanés :</span>
        <div className="flex gap-1">
          {[1, 2, 3, 5, 8, 10].map((n) => (
            <button
              key={n}
              onClick={() => setClientCount(n)}
              className={`w-9 h-9 rounded-lg text-sm font-medium transition-all ${
                clientCount === n
                  ? 'bg-gray-900 text-white shadow-md'
                  : 'bg-white border border-gray-200 text-gray-600 hover:border-gray-300'
              }`}
            >
              {n}
            </button>
          ))}
        </div>
        {clientCount > 1 && (
          <span className="text-sm text-gray-500 ml-2">
            = {totals.recurrentMin * clientCount}-{totals.recurrentMax * clientCount}h/mois
            ({Math.round((totals.recurrentMin * clientCount) / 7.5)}-{Math.round((totals.recurrentMax * clientCount) / 7.5)} jours)
          </span>
        )}
      </motion.div>

      {/* Expertise breakdown */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.35 }}
        className="bg-white rounded-xl border border-gray-200 p-5"
      >
        <h2 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Zap className="w-4 h-4 text-stratos-gold" />
          Répartition par type d'expertise (récurrent)
        </h2>
        <div className="space-y-3">
          {totals.byExpertise.map((exp) => {
            const pct = Math.round(((exp.min + exp.max) / 2) / ((totals.recurrentMin + totals.recurrentMax) / 2) * 100);
            return (
              <div key={exp.key} className="flex items-center gap-3">
                <span className={`text-xs font-medium w-24 ${exp.color}`}>{exp.label}</span>
                <div className="flex-1 h-3 bg-gray-100 rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full ${exp.bg} border ${exp.border}`}
                    style={{ width: `${pct}%`, minWidth: '20px' }}
                  />
                </div>
                <span className="text-sm font-semibold text-gray-700 w-16 text-right">
                  {exp.min}-{exp.max}h
                </span>
                <span className="text-xs text-gray-400 w-10 text-right">{pct}%</span>
              </div>
            );
          })}
        </div>
      </motion.div>

      {/* Onboarding section */}
      <div>
        <button
          onClick={() => setShowOnboarding(!showOnboarding)}
          className="flex items-center gap-2 mb-3"
        >
          <h2 className="text-lg font-bold text-gray-900">
            Onboarding — Mois 1 uniquement
          </h2>
          <span className="text-xs text-gray-400 bg-gray-100 px-2 py-1 rounded-lg">
            {totals.onboardingMin}-{totals.onboardingMax}h
          </span>
          {showOnboarding ? (
            <ChevronUp className="w-4 h-4 text-gray-400" />
          ) : (
            <ChevronDown className="w-4 h-4 text-gray-400" />
          )}
        </button>
        {showOnboarding && (
          <div className="space-y-2">
            {onboardingActivities.map((a, i) => renderActivity(a, i))}
          </div>
        )}
      </div>

      {/* Recurring section */}
      <div>
        <h2 className="text-lg font-bold text-gray-900 mb-3 flex items-center gap-2">
          Récurrent mensuel
          <span className="text-xs text-gray-400 bg-gray-100 px-2 py-1 rounded-lg">
            {totals.recurrentMin}-{totals.recurrentMax}h/mois
          </span>
        </h2>

        {/* Group by category */}
        {['SEO & GEO', 'Google Business', 'Suivi & Reporting', 'Développement Stratos'].map((cat) => {
          const catActivities = recurrentActivities.filter((a) => a.category === cat);
          const catMin = catActivities.reduce((s, a) => s + a.monthlyHoursMin, 0);
          const catMax = catActivities.reduce((s, a) => s + a.monthlyHoursMax, 0);

          return (
            <div key={cat} className="mb-6">
              <div className="flex items-center justify-between mb-2 px-1">
                <h3 className="text-sm font-semibold text-gray-600 uppercase tracking-wide">
                  {cat}
                </h3>
                <span className="text-xs font-medium text-gray-400">
                  {catMin}-{catMax}h
                </span>
              </div>
              <div className="space-y-2">
                {catActivities.map((a, i) => renderActivity(a, i))}
              </div>
            </div>
          );
        })}
      </div>

      {/* ================================================================== */}
      {/* ANALYSE FINANCIÈRE */}
      {/* ================================================================== */}
      <div className="pt-6 border-t-2 border-gray-200">
        <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
            <DollarSign className="w-8 h-8 text-stratos-gold" />
            Analyse financière
          </h1>
          <p className="text-gray-500 mt-1">
            Coût interne, marge et scénarios de scaling par pack
          </p>
        </motion.div>
      </div>

      {/* Pack selector */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.1 }}
        className="flex items-center gap-3 bg-gray-50 rounded-xl p-4"
      >
        <Calculator className="w-5 h-5 text-gray-400" />
        <span className="text-sm text-gray-600">Pack simulé :</span>
        <div className="flex gap-2">
          {(['essentiel', 'premium', 'signature'] as const).map((pack) => (
            <button
              key={pack}
              onClick={() => setSelectedPack(pack)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                selectedPack === pack
                  ? 'bg-gray-900 text-white shadow-md'
                  : 'bg-white border border-gray-200 text-gray-600 hover:border-gray-300'
              }`}
            >
              {PACK_PRICING[pack].label} — <span className="line-through opacity-50">{PACK_PRICING[pack].price.toLocaleString('fr-FR')}€</span> {PACK_PRICING[pack].effectivePrice.toLocaleString('fr-FR')}€ effectif
            </button>
          ))}
        </div>
      </motion.div>

      {/* Financial KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          {
            label: 'Coût client/mois',
            value: `${Math.round(financials.clientCostPerClient)}€`,
            sub: `Tâches dédiées au client`,
            color: 'border-red-300 bg-red-50/50',
            icon: TrendingDown,
          },
          {
            label: 'Coût R&D mutualisé',
            value: `${Math.round(financials.devCostPerClient)}€`,
            sub: `${Math.round(financials.totalDevCost)}€ ÷ ${clientCount} client${clientCount > 1 ? 's' : ''}`,
            color: 'border-purple-300 bg-purple-50/50',
            icon: Code2,
          },
          {
            label: 'Coût total / client',
            value: `${Math.round(financials.totalCostPerClient)}€`,
            sub: `sur ${financials.packPrice.toLocaleString('fr-FR')}€ facturés`,
            color: 'border-orange-300 bg-orange-50/50',
            icon: Calculator,
          },
          {
            label: 'Marge / client',
            value: `${financials.marginPerClient >= 0 ? '+' : ''}${Math.round(financials.marginPerClient)}€`,
            sub: `${Math.round(financials.marginPct)}% de marge`,
            color: financials.marginPct > 30 ? 'border-emerald-300 bg-emerald-50/50' : financials.marginPct > 0 ? 'border-amber-300 bg-amber-50/50' : 'border-red-300 bg-red-50/50',
            icon: financials.marginPct > 30 ? CheckCircle2 : financials.marginPct > 0 ? AlertTriangle : TrendingDown,
          },
        ].map((kpi, i) => {
          const KpiIcon = kpi.icon;
          return (
            <motion.div
              key={kpi.label}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.15 + i * 0.05 }}
              className={`rounded-xl border-2 ${kpi.color} p-4`}
            >
              <div className="flex items-center justify-between mb-1">
                <KpiIcon className="w-4 h-4 text-gray-400" />
              </div>
              <div className="text-2xl font-bold text-gray-900">{kpi.value}</div>
              <div className="text-xs text-gray-500 mt-1">{kpi.label}</div>
              <div className="text-[10px] text-gray-400 mt-0.5">{kpi.sub}</div>
            </motion.div>
          );
        })}
      </div>

      {/* Global P&L for current client count */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.3 }}
        className="bg-white rounded-xl border border-gray-200 p-5"
      >
        <h2 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <BarChart3 className="w-4 h-4 text-stratos-gold" />
          P&L mensuel — {clientCount} client{clientCount > 1 ? 's' : ''} × {PACK_PRICING[selectedPack].label}
        </h2>
        <div className="space-y-3">
          <div className="flex items-center justify-between py-2 border-b border-gray-100">
            <span className="text-sm text-gray-600">Revenus ({clientCount} × {financials.packPrice.toLocaleString('fr-FR')}€)</span>
            <span className="text-sm font-bold text-emerald-600">+{financials.totalRevenue.toLocaleString('fr-FR')}€</span>
          </div>
          <div className="flex items-center justify-between py-2 border-b border-gray-100">
            <span className="text-sm text-gray-600">Coûts clients ({clientCount} × {Math.round(financials.clientCostPerClient)}€)</span>
            <span className="text-sm font-medium text-red-500">-{Math.round(financials.clientCostPerClient * clientCount).toLocaleString('fr-FR')}€</span>
          </div>
          <div className="flex items-center justify-between py-2 border-b border-gray-100">
            <span className="text-sm text-gray-600">Coûts R&D Stratos (mutualisés)</span>
            <span className="text-sm font-medium text-red-500">-{Math.round(financials.totalDevCost).toLocaleString('fr-FR')}€</span>
          </div>
          <div className="flex items-center justify-between py-3 bg-gray-50 rounded-lg px-3 -mx-1">
            <span className="text-sm font-bold text-gray-900">Marge nette</span>
            <div className="text-right">
              <span className={`text-lg font-bold ${financials.totalMargin >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                {financials.totalMargin >= 0 ? '+' : ''}{Math.round(financials.totalMargin).toLocaleString('fr-FR')}€
              </span>
              <span className={`text-xs ml-2 ${financials.totalMarginPct >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>
                ({Math.round(financials.totalMarginPct)}%)
              </span>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Coût par niveau de difficulté */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.35 }}
        className="bg-white rounded-xl border border-gray-200 p-5"
      >
        <h2 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Users className="w-4 h-4 text-stratos-gold" />
          Répartition des coûts par niveau d'expertise (tâches client uniquement)
        </h2>
        <div className="space-y-3">
          {financials.byDifficulty.map((d) => {
            const pct = financials.clientCostPerClient > 0 ? Math.round((d.cost / financials.clientCostPerClient) * 100) : 0;
            return (
              <div key={d.level} className="flex items-center gap-3">
                <span className={`text-xs font-medium w-20 ${d.color}`}>{d.label}</span>
                <span className="text-[10px] text-gray-400 w-12">{d.rate}€/h</span>
                <div className="flex-1 h-3 bg-gray-100 rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full ${d.bg}`}
                    style={{ width: `${pct}%`, minWidth: '12px' }}
                  />
                </div>
                <span className="text-xs text-gray-600 w-12 text-right font-medium">{d.hours.toFixed(1)}h</span>
                <span className="text-sm font-bold text-gray-800 w-16 text-right">{Math.round(d.cost)}€</span>
                <span className="text-xs text-gray-400 w-10 text-right">{pct}%</span>
              </div>
            );
          })}
          <div className="flex items-center justify-between pt-2 border-t border-gray-200 mt-2">
            <span className="text-xs font-medium text-gray-500">Total client</span>
            <span className="text-sm font-bold text-gray-900">{Math.round(financials.clientCostPerClient)}€/mois</span>
          </div>
        </div>
      </motion.div>

      {/* Tâches délégables */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.4 }}
        className="bg-emerald-50 rounded-xl border border-emerald-200 p-5"
      >
        <h2 className="text-sm font-semibold text-emerald-800 mb-3 flex items-center gap-2">
          <UserPlus className="w-4 h-4 text-emerald-600" />
          Potentiel de délégation — Tâches externalisables
        </h2>
        <p className="text-xs text-emerald-700 mb-4">
          Ces tâches peuvent être confiées à un junior, un freelance, ou un stagiaire pour réduire le coût interne
          et libérer du temps fondateur sur les tâches à haute valeur ajoutée.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {financials.delegatable.map((a) => (
            <div key={a.id} className="flex items-center justify-between bg-white rounded-lg p-3 border border-emerald-100">
              <div className="flex items-center gap-2">
                <a.icon className="w-4 h-4 text-emerald-500" />
                <span className="text-sm text-gray-700">{a.label}</span>
              </div>
              <div className="text-right">
                <span className="text-xs font-medium text-gray-600">{a.avgHours.toFixed(1)}h</span>
                <span className="text-xs text-gray-400 ml-2">~{Math.round(a.cost)}€</span>
              </div>
            </div>
          ))}
        </div>
        <div className="mt-4 flex items-center justify-between bg-emerald-100 rounded-lg p-3">
          <span className="text-sm font-medium text-emerald-800">
            Total délégable : {financials.delegatableHours.toFixed(1)}h/mois · ~{Math.round(financials.delegatableCost)}€
          </span>
          <span className="text-xs text-emerald-600">
            {Math.round((financials.delegatableHours / (financials.clientActivities.reduce((s, a) => s + a.avgHours, 0) || 1)) * 100)}% du volume client
          </span>
        </div>
      </motion.div>

      {/* ================================================================== */}
      {/* SCÉNARIOS DE SCALING */}
      {/* ================================================================== */}
      <div className="pt-6 border-t-2 border-gray-200">
        <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
            <Building2 className="w-8 h-8 text-stratos-gold" />
            Scénarios de scaling
          </h1>
          <p className="text-gray-500 mt-1">
            Projection financière et besoins RH selon le nombre de clients — {PACK_PRICING[selectedPack].label}
          </p>
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.2 }}
        className="bg-white rounded-xl border border-gray-200 overflow-hidden"
      >
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Clients</th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">CA mensuel</th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Coût total</th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Marge</th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Marge %</th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Heures/mois</th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">ETP nécessaires</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Équipe type</th>
              </tr>
            </thead>
            <tbody>
              {financials.scalingScenarios.map((s, i) => {
                const teamDesc = s.ftEquivalent <= 1.2
                  ? 'Fondateur seul'
                  : s.ftEquivalent <= 2
                    ? 'Fondateur + 1 junior/stagiaire'
                    : s.ftEquivalent <= 3.5
                      ? 'Fondateur + 1 confirmé + 1 junior'
                      : s.ftEquivalent <= 5
                        ? '2 seniors + 2 juniors'
                        : '2-3 seniors + 3 juniors + 1 freelance dev';

                return (
                  <tr
                    key={s.clients}
                    className={`border-b border-gray-100 ${i % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'} hover:bg-amber-50/30 transition-colors`}
                  >
                    <td className="px-4 py-3">
                      <span className="font-bold text-gray-900">{s.clients}</span>
                      <span className="text-gray-400 ml-1">clients</span>
                    </td>
                    <td className="px-4 py-3 text-right font-medium text-emerald-600">
                      {s.revenue.toLocaleString('fr-FR')}€
                    </td>
                    <td className="px-4 py-3 text-right font-medium text-red-500">
                      {Math.round(s.cost).toLocaleString('fr-FR')}€
                    </td>
                    <td className={`px-4 py-3 text-right font-bold ${s.margin >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                      {s.margin >= 0 ? '+' : ''}{Math.round(s.margin).toLocaleString('fr-FR')}€
                    </td>
                    <td className="px-4 py-3 text-right">
                      <span className={`inline-flex px-2 py-0.5 rounded text-xs font-bold ${
                        s.marginP > 50 ? 'bg-emerald-100 text-emerald-700' :
                        s.marginP > 30 ? 'bg-emerald-50 text-emerald-600' :
                        s.marginP > 10 ? 'bg-amber-50 text-amber-600' :
                        'bg-red-50 text-red-600'
                      }`}>
                        {Math.round(s.marginP)}%
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right text-gray-700 font-medium">
                      {Math.round(s.totalHours)}h
                    </td>
                    <td className="px-4 py-3 text-right">
                      <span className="font-bold text-gray-900">{s.ftEquivalent.toFixed(1)}</span>
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-500 max-w-[200px]">
                      {teamDesc}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </motion.div>

      {/* ================================================================== */}
      {/* COMPTE D'EXPLOITATION ESTIMATIF */}
      {/* ================================================================== */}
      <div className="pt-6 border-t-2 border-gray-200">
        <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
            <FileText className="w-8 h-8 text-stratos-gold" />
            Compte de résultat prévisionnel
          </h1>
          <p className="text-gray-500 mt-1">
            Projection sur 24 mois — Startup mode, croissance réaliste
          </p>
        </motion.div>
      </div>

      {/* Hypothèses clés — enrichies */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.1 }}
        className="bg-amber-50 rounded-xl border border-amber-200 p-5"
      >
        <h2 className="text-sm font-semibold text-amber-800 mb-3 flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 text-amber-600" />
          Hypothèses du modèle
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-xs text-amber-700">
          {[
            { val: '15%', label: 'Churn annuel', sub: '~1.25%/mois — engagements 6-12 mois, premium' },
            { val: '1→2', label: 'Nouveaux clients/mois', sub: '1/mois M1-M9, 1.5/mois M10-M18, 2/mois M18+' },
            { val: '2 100€', label: 'Panier moyen mensuel', sub: '10% Essentiel, 65% Premium, 25% Signature' },
            { val: '4 000€', label: 'Audit 360 one-shot', sub: 'Facturé à chaque nouveau client' },
            { val: '30%', label: 'Refonte site web', sub: '30% des clients acceptent une refonte (3-5K€ one-shot)' },
            { val: '-15%/an', label: 'Courbe d\'apprentissage', sub: 'Le temps/client baisse de 15% par an (process, outils)' },
            { val: '70%', label: 'Outils dev interne', sub: '70% des outils SEO/GEO dev en interne → réduit CAPEX' },
            { val: '10%', label: 'Commission commerciale', sub: 'Apporteur d\'affaires : 10% du CA M1-M6 du client apporté' },
          ].map((h) => (
            <div key={h.label} className="bg-white rounded-lg p-3 border border-amber-100">
              <div className="font-bold text-amber-900 text-lg">{h.val}</div>
              <div>{h.label}</div>
              <div className="text-[10px] text-amber-500 mt-1">{h.sub}</div>
            </div>
          ))}
        </div>
      </motion.div>

      {/* CAPEX, OPEX & Commercial détaillés */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.15 }}
        className="grid grid-cols-1 md:grid-cols-3 gap-4"
      >
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <h2 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
            <Server className="w-4 h-4 text-blue-500" />
            CAPEX — Outils & licences
          </h2>
          <div className="space-y-2">
            {[
              { tool: 'Semrush (Guru)', cost: '230€', note: 'Mots-clés, positions, audits' },
              { tool: 'Screaming Frog', cost: '17€', note: '199€/an — crawl technique' },
              { tool: 'DeepL Pro', cost: '25€', note: 'Traduction IA multilingue' },
              { tool: 'Brevo (Sendinblue)', cost: '19€', note: 'Campagnes email 20K/mois' },
              { tool: 'Supabase Pro', cost: '25€', note: 'BDD + auth app Stratos' },
              { tool: 'Vercel Pro', cost: '20€', note: 'Hosting app + sites clients' },
              { tool: 'Google Workspace', cost: '12€', note: 'Email, Drive, Agenda' },
              { tool: 'Domaines & DNS', cost: '10€', note: '~2 domaines' },
              { tool: 'Claude / OpenAI API', cost: '30€', note: 'Simulations IA, chatbot' },
            ].map((item) => (
              <div key={item.tool} className="flex items-center justify-between py-1 border-b border-gray-50">
                <div>
                  <span className="text-xs font-medium text-gray-800">{item.tool}</span>
                  <span className="text-[10px] text-gray-400 ml-1">{item.note}</span>
                </div>
                <span className="text-[11px] font-bold text-gray-700 whitespace-nowrap">{item.cost}</span>
              </div>
            ))}
            <div className="flex items-center justify-between pt-2 border-t border-gray-200">
              <span className="text-xs font-bold text-gray-900">Total CAPEX</span>
              <span className="text-sm font-bold text-blue-600">~388€/mois</span>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-orange-200 p-5">
          <h2 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
            <Target className="w-4 h-4 text-orange-500" />
            Commercial & Démarchage
          </h2>
          <div className="space-y-2">
            {[
              { item: 'Déplacements prospection', cost: '150-400€', note: 'RDV prospects, visites restaurants, hôtels' },
              { item: 'Repas & représentation', cost: '100-200€', note: 'Déjeuners prospects, invitations, café' },
              { item: 'Supports commerciaux', cost: '30-50€', note: 'Cartes de visite, présentations, goodies' },
              { item: 'CRM / Pipedrive', cost: '15€', note: 'Suivi pipeline commercial' },
              { item: 'LinkedIn Sales Navigator', cost: '80€', note: 'Prospection ciblée décideurs F&B' },
              { item: 'Événements / salons', cost: '50-150€', note: 'Sirha, salons hôtellerie, networking' },
              { item: 'Commission apporteurs', cost: 'Variable', note: '10% CA M1-M6 du client apporté' },
            ].map((item) => (
              <div key={item.item} className="flex items-center justify-between py-1 border-b border-gray-50">
                <div>
                  <span className="text-xs font-medium text-gray-800">{item.item}</span>
                  <span className="text-[10px] text-gray-400 ml-1">{item.note}</span>
                </div>
                <span className="text-[11px] font-bold text-gray-700 whitespace-nowrap">{item.cost}</span>
              </div>
            ))}
            <div className="flex items-center justify-between pt-2 border-t border-orange-200">
              <span className="text-xs font-bold text-gray-900">Total Commercial (hors commissions)</span>
              <span className="text-sm font-bold text-orange-600">~425-895€/mois</span>
            </div>
            <div className="mt-2 p-2 bg-orange-50 rounded-lg border border-orange-100">
              <p className="text-[10px] text-orange-700">
                <strong>Note :</strong> Le démarchage est principalement porté par le fondateur en Phase 1-2.
                Les commissions apporteurs démarrent en Phase 2 (10% sur 6 mois de CA).
                Un commercial freelance entre en Phase 4 (100% commission).
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <h2 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
            <Building2 className="w-4 h-4 text-purple-500" />
            OPEX — Fonctionnement
          </h2>
          <div className="space-y-2">
            {[
              { item: 'Déplacements clients', cost: '100-200€', note: 'RDV suivi clients existants' },
              { item: 'Comptable', cost: '100€', note: 'Comptabilité en ligne (Indy, Dougs)' },
              { item: 'Assurance RC Pro', cost: '40€', note: '~480€/an — obligatoire' },
              { item: 'Coworking / bureau', cost: '0-250€', note: 'Home office au démarrage' },
              { item: 'Téléphone & internet', cost: '50€', note: 'Forfait pro + fibre' },
              { item: 'Rédaction SEO freelance', cost: '80-160€', note: '1-2 articles à 80€ (prix 2026)' },
              { item: 'Frais bancaires', cost: '15€', note: 'Compte pro (Qonto, Shine)' },
              { item: 'Formation & veille', cost: '50€', note: 'Conférences, certifications' },
            ].map((item) => (
              <div key={item.item} className="flex items-center justify-between py-1 border-b border-gray-50">
                <div>
                  <span className="text-xs font-medium text-gray-800">{item.item}</span>
                  <span className="text-[10px] text-gray-400 ml-1">{item.note}</span>
                </div>
                <span className="text-[11px] font-bold text-gray-700 whitespace-nowrap">{item.cost}</span>
              </div>
            ))}
            <div className="flex items-center justify-between pt-2 border-t border-gray-200">
              <span className="text-xs font-bold text-gray-900">Total OPEX</span>
              <span className="text-sm font-bold text-purple-600">~435-865€/mois</span>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Tableau d'exploitation complet */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.2 }}
        className="bg-white rounded-xl border border-gray-200 overflow-hidden"
      >
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-gray-900 text-white">
                <th className="text-left px-3 py-3 font-semibold sticky left-0 bg-gray-900 z-10 min-w-[260px]">Ligne</th>
                {[3, 6, 9, 12, 18, 24].map((m) => (
                  <th key={m} className="text-right px-3 py-3 font-semibold whitespace-nowrap min-w-[90px]">M{m}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {(() => {
                const months = [3, 6, 9, 12, 18, 24];
                const churnMonthly = 0.0125;
                // Mix effectif: 10% Essentiel 850€, 65% Premium 1700€, 25% Signature 3000€
                // (après remise fidélité 15% sur 12 mois + crédits parrainage ~5%)
                const avgPackPrice = 0.10 * 850 + 0.65 * 1700 + 0.25 * 3000; // = 1 940€
                // Acquisition réaliste : ramp-up commercial B2B luxe = 3-6 mois
                const newClientsPerMonth = (m: number) =>
                  m <= 3 ? 0.3 :    // Pipeline se construit, réseau personnel
                  m <= 6 ? 0.5 :    // Premières closes, bouche-à-oreille
                  m <= 9 ? 0.8 :    // Commercial rodé, premiers cas à montrer
                  m <= 12 ? 1.0 :   // Pipeline mature, référencement
                  m <= 18 ? 1.2 :   // Notoriété + referrals actifs
                  1.5;              // Scale avec marque établie
                // 30% des nouveaux acceptent une refonte (one-shot 4000€ en moyenne)
                const refonteRate = 0.30;
                const refontePriceAvg = 4000;
                // Courbe d'apprentissage : -15% par an sur le coût horaire effectif
                const learningFactor = (m: number) => Math.max(0.70, 1 - 0.15 * (m / 12));

                const getActiveClients = (month: number) => {
                  let active = 0;
                  for (let i = 1; i <= month; i++) {
                    active = active * (1 - churnMonthly) + newClientsPerMonth(i);
                  }
                  return Math.round(active * 10) / 10;
                };

                type TeamProfile = { role: string; brut: number; charged: number; variable: number; type: 'salarie' | 'alternant' | 'commercial' };
                const getTeam = (m: number, clients: number): { profiles: TeamProfile[]; freelanceRedac: number } => {
                  // ⚡ Logique basée sur le NOMBRE DE CLIENTS + mois
                  // Fondateur = CEO/stratégie/production senior, PAS commercial terrain
                  // Business Developer dès M1 avec base modeste + variable agressif

                  const profiles: TeamProfile[] = [];
                  const newClients = newClientsPerMonth(m);

                  // ── FONDATEUR (CEO / production senior) ──
                  // 1 500€/mois pendant 12 mois (revenus extérieurs, pérenniser la structure)
                  // Puis montée progressive une fois la rentabilité atteinte
                  const fondateurBrut = m <= 12 ? 1500 : m <= 18 ? 2500 : 3500;
                  profiles.push({ role: 'Fondateur / CEO', brut: fondateurBrut, charged: Math.round(fondateurBrut * 1.4), variable: 0, type: 'salarie' });

                  // ── BUSINESS DEVELOPER dès M1 ──
                  // Base modeste (1 800€ brut) + variable : 10% du CA signé sur 12 premiers mois
                  // En agence SEO B2B : cycle 2-4 mois, ramp-up 3-6 mois
                  const bizdevBase = 1800;
                  const bizdevCharged = Math.round(bizdevBase * 1.4); // ~2 520€ chargé
                  // Variable = 10% du CA annuel des nouveaux contrats signés ce mois
                  // Un contrat Premium 1700€ × 12 mois = 20 400€ CA → 2 040€ commission
                  // Mensualisé : newClients × avgPackPrice × 12 × 10% / 12
                  const bizdevVariable = Math.round(newClients * avgPackPrice * 0.10);
                  profiles.push({ role: 'Business Developer', brut: bizdevBase, charged: bizdevCharged, variable: bizdevVariable, type: 'commercial' });

                  // ── ALTERNANT SEO/Content dès M3 ──
                  if (m >= 3) {
                    profiles.push({ role: 'Alternant SEO/Content', brut: 900, charged: 1000, variable: 0, type: 'alternant' });
                  }

                  // ── CHARGÉ(E) SEO JUNIOR dès 3-4 clients ── INDISPENSABLE
                  // 3 clients × ~45h = 135h → fondateur saturé (il fait aussi stratégie + dev)
                  if (clients >= 3) {
                    profiles.push({ role: 'Chargé(e) SEO Junior', brut: 2100, charged: 2940, variable: 0, type: 'salarie' });
                  }

                  // ── CONSULTANT SEO/GEO CONFIRMÉ dès 7 clients ──
                  if (clients >= 7) {
                    profiles.push({ role: 'Consultant SEO/GEO Confirmé', brut: 3000, charged: 4200, variable: 0, type: 'salarie' });
                    const altIdx = profiles.findIndex(p => p.role === 'Alternant SEO/Content');
                    if (altIdx >= 0) profiles[altIdx].role = 'Alternant GBP & Content';
                  }

                  // ── 2ÈME CHARGÉ(E) SEO dès 12 clients ──
                  if (clients >= 12) {
                    profiles.push({ role: 'Chargé(e) SEO Confirmé(e)', brut: 2500, charged: 3500, variable: 0, type: 'salarie' });
                  }

                  return {
                    profiles,
                    freelanceRedac: Math.min(clients, 8) * 80,
                  };
                };

                type Row = { label: string; values: (string | number)[]; bold?: boolean; bg?: string; color?: string; separator?: boolean; indent?: boolean };
                const rows: Row[] = [];

                const monthData = months.map((m) => {
                  const clients = getActiveClients(m);
                  const displayClients = Math.round(clients);
                  const newClients = newClientsPerMonth(m);
                  const lf = learningFactor(m);

                  // === REVENUS ===
                  const recurringRevenue = Math.round(clients * avgPackPrice);
                  const auditRevenue = Math.round(newClients * 4000);
                  const refonteRevenue = Math.round(newClients * refonteRate * refontePriceAvg);
                  const totalRevenue = recurringRevenue + auditRevenue + refonteRevenue;

                  // === CHARGES ===
                  const team = getTeam(m, displayClients);
                  const totalSalaryFixed = team.profiles.reduce((s, p) => s + p.charged, 0);
                  const totalVariable = team.profiles.reduce((s, p) => s + p.variable, 0);
                  const totalSalary = totalSalaryFixed + totalVariable;
                  const freelanceRedac = team.freelanceRedac;

                  // CAPEX
                  const capexBase = 388;
                  const capexScaling = displayClients > 5 ? 50 : 0;
                  const capex = capexBase + capexScaling;

                  // COMMERCIAL & DÉMARCHAGE (frais hors salaire BizDev)
                  const deplProspection = Math.min(150 + Math.round(newClients * 100), 400);
                  const repasProspection = Math.min(100 + Math.round(newClients * 50), 200);
                  const supportsComm = 40;
                  const crmPipedrive = 15;
                  const linkedInSales = m >= 3 ? 80 : 0; // Sales Navigator dès M3 (outil BizDev)
                  const evenementsSalons = m >= 6 ? Math.min(50 + Math.round(m * 5), 150) : 0;
                  // Apporteurs d'affaires externes : 10% CA sur 6 premiers mois (~15% des nouveaux clients viennent par apporteur)
                  const apporteursCommission = Math.round(newClients * 0.15 * avgPackPrice * 0.10 * 6 / 12);
                  const totalCommercial = deplProspection + repasProspection + supportsComm + crmPipedrive + linkedInSales + evenementsSalons + apporteursCommission;

                  // OPEX (hors commercial)
                  const deplClients = Math.min(100 + displayClients * 20, 300);
                  const comptable = 100;
                  const assurance = 40;
                  const coworking = m >= 12 ? 200 : 0;
                  const telInternet = 50;
                  const fraisBancaires = 15;
                  const formation = 50;
                  const opex = deplClients + comptable + assurance + coworking + telInternet + fraisBancaires + formation;

                  const totalCharges = totalSalary + freelanceRedac + totalCommercial + capex + opex;
                  const ebitda = totalRevenue - totalCharges;
                  const ebitdaPct = totalRevenue > 0 ? Math.round((ebitda / totalRevenue) * 100) : 0;

                  // Heures effectives (avec courbe d'apprentissage)
                  const clientHoursBase = financials.clientActivities.reduce((s, a) => s + (a.monthlyHoursMin + a.monthlyHoursMax) / 2, 0);
                  const devHours = financials.devActivities.reduce((s, a) => s + (a.monthlyHoursMin + a.monthlyHoursMax) / 2, 0);
                  const totalHours = Math.round((clientHoursBase * lf * displayClients) + devHours);

                  return {
                    m, displayClients, newClients, lf,
                    recurringRevenue, auditRevenue, refonteRevenue, totalRevenue,
                    totalSalaryFixed, totalVariable, totalSalary, freelanceRedac,
                    deplProspection, repasProspection, supportsComm, crmPipedrive, linkedInSales, evenementsSalons, apporteursCommission, totalCommercial,
                    capex, opex,
                    deplClients, comptable, assurance, coworking, telInternet, fraisBancaires, formation,
                    totalCharges, ebitda, ebitdaPct, totalHours, team,
                  };
                });

                const fmt = (n: number) => n.toLocaleString('fr-FR');

                // REVENUS
                rows.push({ label: 'REVENUS', values: months.map(() => ''), bold: true, bg: 'bg-emerald-50' });
                rows.push({ label: 'Clients actifs', values: monthData.map(d => d.displayClients), indent: true });
                rows.push({ label: 'Nouveaux/mois', values: monthData.map(d => d.newClients), indent: true });
                rows.push({ label: 'Courbe apprentissage', values: monthData.map(d => `${Math.round(d.lf * 100)}%`), indent: true });
                rows.push({ label: `CA récurrent (moy. effectif ${Math.round(avgPackPrice)}€)`, values: monthData.map(d => `${fmt(d.recurringRevenue)}€`), color: 'text-emerald-600', indent: true });
                rows.push({ label: 'Audits 360 (4 000€ × nouveaux)', values: monthData.map(d => `${fmt(d.auditRevenue)}€`), color: 'text-emerald-600', indent: true });
                rows.push({ label: 'Refontes site (30% × ~4 000€)', values: monthData.map(d => `${fmt(d.refonteRevenue)}€`), color: 'text-emerald-600', indent: true });
                rows.push({ label: 'TOTAL REVENUS', values: monthData.map(d => `${fmt(d.totalRevenue)}€`), bold: true, color: 'text-emerald-700', bg: 'bg-emerald-50', separator: true });

                // MASSE SALARIALE (fixe + variable)
                rows.push({ label: 'MASSE SALARIALE', values: months.map(() => ''), bold: true, bg: 'bg-red-50' });
                const allRoles = [...new Set(monthData.flatMap(d => d.team.profiles.map(p => p.role)))];
                allRoles.forEach((role) => {
                  rows.push({
                    label: role,
                    values: monthData.map(d => {
                      const p = d.team.profiles.find(p => p.role === role);
                      if (!p) return '—';
                      if (p.variable > 0) return `${fmt(p.charged)}€ + ${fmt(p.variable)}€ var.`;
                      return `${fmt(p.charged)}€`;
                    }),
                    color: 'text-red-400',
                    indent: true,
                  });
                });
                rows.push({ label: 'Fixe chargé', values: monthData.map(d => `${fmt(d.totalSalaryFixed)}€`), color: 'text-red-400', indent: true });
                rows.push({ label: 'Variable commercial', values: monthData.map(d => `${fmt(d.totalVariable)}€`), color: 'text-red-400', indent: true });
                rows.push({ label: 'Total masse salariale', values: monthData.map(d => `${fmt(d.totalSalary)}€`), bold: true, color: 'text-red-500' });

                // COMMERCIAL & DÉMARCHAGE
                rows.push({ label: 'COMMERCIAL & DÉMARCHAGE', values: months.map(() => ''), bold: true, bg: 'bg-orange-50' });
                rows.push({ label: 'Déplacements prospection', values: monthData.map(d => `${fmt(d.deplProspection)}€`), color: 'text-orange-400', indent: true });
                rows.push({ label: 'Repas & représentation prospects', values: monthData.map(d => `${fmt(d.repasProspection)}€`), color: 'text-orange-400', indent: true });
                rows.push({ label: 'Supports comm. + CRM Pipedrive', values: monthData.map(d => `${fmt(d.supportsComm + d.crmPipedrive)}€`), color: 'text-orange-400', indent: true });
                rows.push({ label: 'LinkedIn Sales Navigator', values: monthData.map(d => d.linkedInSales > 0 ? `${fmt(d.linkedInSales)}€` : '—'), color: 'text-orange-400', indent: true });
                rows.push({ label: 'Événements & salons', values: monthData.map(d => d.evenementsSalons > 0 ? `${fmt(d.evenementsSalons)}€` : '—'), color: 'text-orange-400', indent: true });
                rows.push({ label: 'Apporteurs externes (10% CA × 15% clients)', values: monthData.map(d => d.apporteursCommission > 0 ? `${fmt(d.apporteursCommission)}€` : '—'), color: 'text-orange-400', indent: true });
                rows.push({ label: 'Total Commercial & Démarchage', values: monthData.map(d => `${fmt(d.totalCommercial)}€`), bold: true, color: 'text-orange-600' });

                // SOUS-TRAITANCE
                rows.push({ label: 'SOUS-TRAITANCE', values: months.map(() => ''), bold: true, bg: 'bg-pink-50' });
                rows.push({ label: 'Rédaction SEO freelance (80-120€/article)', values: monthData.map(d => `${fmt(d.freelanceRedac)}€`), color: 'text-pink-500', indent: true });

                // CAPEX
                rows.push({ label: 'CAPEX — Outils & licences', values: months.map(() => ''), bold: true, bg: 'bg-blue-50' });
                rows.push({ label: 'Stack SaaS (Semrush, DeepL, Brevo, Supabase...)', values: monthData.map(d => `${fmt(d.capex)}€`), color: 'text-blue-500', indent: true });

                // OPEX
                rows.push({ label: 'OPEX — Fonctionnement (hors commercial)', values: months.map(() => ''), bold: true, bg: 'bg-purple-50' });
                rows.push({ label: 'Déplacements clients existants', values: monthData.map(d => `${fmt(d.deplClients)}€`), color: 'text-purple-400', indent: true });
                rows.push({ label: 'Comptable + assurance + banque', values: monthData.map(d => `${fmt(d.comptable + d.assurance + d.fraisBancaires)}€`), color: 'text-purple-400', indent: true });
                rows.push({ label: 'Coworking / bureau', values: monthData.map(d => d.coworking > 0 ? `${fmt(d.coworking)}€` : '—'), color: 'text-purple-400', indent: true });
                rows.push({ label: 'Téléphone + internet + formation', values: monthData.map(d => `${fmt(d.telInternet + d.formation)}€`), color: 'text-purple-400', indent: true });
                rows.push({ label: 'Total OPEX', values: monthData.map(d => `${fmt(d.opex)}€`), color: 'text-purple-500', indent: true });

                // TOTAUX
                rows.push({ label: 'TOTAL CHARGES', values: monthData.map(d => `${fmt(d.totalCharges)}€`), bold: true, color: 'text-red-600', bg: 'bg-red-50', separator: true });
                rows.push({ label: '', values: months.map(() => ''), bg: 'bg-gray-50' });
                rows.push({ label: 'RÉSULTAT (EBITDA)', values: monthData.map(d => `${d.ebitda >= 0 ? '+' : ''}${fmt(d.ebitda)}€`), bold: true, color: 'text-gray-900', bg: 'bg-gray-100' });
                rows.push({ label: 'Marge EBITDA', values: monthData.map(d => `${d.ebitdaPct}%`), bold: true, bg: 'bg-gray-100' });
                rows.push({ label: 'Heures totales/mois', values: monthData.map(d => `${d.totalHours}h`), bg: 'bg-gray-50' });
                rows.push({ label: 'ETP nécessaires (140h utiles)', values: monthData.map(d => `${(d.totalHours / 140).toFixed(1)}`), bg: 'bg-gray-50' });

                return rows.map((row, ri) => (
                  <tr key={ri} className={`${row.bg || ''} ${row.separator ? 'border-t-2 border-gray-300' : 'border-b border-gray-100'}`}>
                    <td className={`px-3 py-2 ${row.bold ? 'font-bold text-gray-900' : 'text-gray-600'} ${row.indent ? 'pl-6' : ''} sticky left-0 ${row.bg || 'bg-white'} z-10 whitespace-nowrap`}>
                      {row.label}
                    </td>
                    {row.values.map((v, vi) => (
                      <td key={vi} className={`px-3 py-2 text-right ${row.bold ? 'font-bold' : 'font-medium'} ${row.color || 'text-gray-700'} whitespace-nowrap`}>
                        {v}
                      </td>
                    ))}
                  </tr>
                ));
              })()}
            </tbody>
          </table>
        </div>
      </motion.div>

      {/* Team evolution detail */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.3 }}
        className="bg-white rounded-xl border border-gray-200 p-5"
      >
        <h2 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Users className="w-4 h-4 text-stratos-gold" />
          Évolution de l'équipe par phase
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[
            {
              phase: 'Phase 1',
              period: 'M1 → M6',
              clients: '1-5',
              team: [
                { role: 'Fondateur / CEO', salary: '2 500€ brut', scope: 'Production, R&D, commercial, admin' },
                { role: 'Alternant SEO/Content', salary: '900€ brut', scope: 'GBP, avis, contenu, suivi — dès M3' },
              ],
              commercial: 'Fondateur + réseau perso',
              color: 'border-blue-200 bg-blue-50/30',
              headerColor: 'text-blue-700',
            },
            {
              phase: 'Phase 2',
              period: 'M6 → M12',
              clients: '5-8',
              team: [
                { role: 'Fondateur / CEO', salary: '3 000€ brut', scope: 'Stratégie, R&D IA, clients premium' },
                { role: 'Chargé(e) SEO Junior', salary: '2 100€ brut', scope: 'GBP, landing pages, reporting, suivi' },
                { role: 'Alternant SEO/Content', salary: '900€ brut', scope: 'Avis, contenu, traductions, GBP' },
              ],
              commercial: 'Apporteurs d\'affaires (10% commission)',
              color: 'border-emerald-200 bg-emerald-50/30',
              headerColor: 'text-emerald-700',
            },
            {
              phase: 'Phase 3',
              period: 'M12 → M18',
              clients: '8-13',
              team: [
                { role: 'Fondateur / CEO', salary: '3 500€ brut', scope: 'Stratégie, biz dev, R&D, grands comptes' },
                { role: 'Consultant SEO/GEO Confirmé', salary: '3 000€ brut', scope: 'GEO, audits, suivi IA, clients' },
                { role: 'Chargé(e) SEO Junior', salary: '2 100€ brut', scope: 'Production SEO, landing pages' },
                { role: 'Alternant GBP & Content', salary: '900€ brut', scope: 'GBP, avis, contenu, réseaux' },
              ],
              commercial: 'Apporteurs + 1er commercial freelance',
              color: 'border-purple-200 bg-purple-50/30',
              headerColor: 'text-purple-700',
            },
            {
              phase: 'Phase 4',
              period: 'M18+',
              clients: '13+',
              team: [
                { role: 'Fondateur / CEO', salary: '4 000€ brut', scope: 'Vision, grands comptes, partenariats' },
                { role: 'Consultant SEO/GEO Confirmé', salary: '3 000€ brut', scope: 'GEO, audits, stratégie clients' },
                { role: 'Chargé(e) SEO Junior', salary: '2 300€ brut', scope: 'Production SEO, suivi, landing pages' },
                { role: 'Alternant GBP & Content', salary: '900€ brut', scope: 'GBP, avis, contenu' },
                { role: 'Commercial freelance', salary: 'Commission 10%', scope: 'Prospection, networking, closings' },
              ],
              commercial: 'Commercial freelance + réseau partenaires',
              color: 'border-amber-200 bg-amber-50/30',
              headerColor: 'text-amber-700',
            },
          ].map((phase) => (
            <div key={phase.phase} className={`rounded-xl border ${phase.color} p-4`}>
              <div className="mb-3">
                <h3 className={`font-bold text-sm ${phase.headerColor}`}>{phase.phase}</h3>
                <p className="text-[10px] text-gray-500">{phase.period} · {phase.clients} clients</p>
              </div>
              <div className="space-y-2">
                {phase.team.map((member) => (
                  <div key={member.role} className="bg-white rounded-lg p-2 border border-gray-100">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-semibold text-gray-800">{member.role}</span>
                      <span className="text-[10px] font-bold text-gray-600">{member.salary}</span>
                    </div>
                    <p className="text-[10px] text-gray-400 mt-0.5">{member.scope}</p>
                  </div>
                ))}
                <div className="text-[10px] text-gray-500 pt-1 border-t border-gray-100">
                  Commercial : {phase.commercial}
                </div>
              </div>
            </div>
          ))}
        </div>
      </motion.div>

      {/* Points clés du modèle */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.35 }}
        className="bg-white rounded-xl border border-gray-200 p-5"
      >
        <h2 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Zap className="w-4 h-4 text-stratos-gold" />
          Points clés du modèle économique
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[
            {
              title: 'Courbe d\'apprentissage',
              desc: 'Le temps passé par client diminue de ~15% par an grâce aux process, templates et outils internes. Un client en M12 coûte ~85% du temps d\'un client en M1.',
              icon: TrendingDown, iconColor: 'text-blue-500',
            },
            {
              title: 'Refonte site web (30%)',
              desc: 'Environ 30% des clients premium acceptent une refonte de site (3-5K€ one-shot). Revenue additionnel non récurrent mais à forte marge, exécutable par le fondateur + alternant.',
              icon: Layout, iconColor: 'text-emerald-500',
            },
            {
              title: 'Outils internes (70%)',
              desc: 'Développer 70% des outils en interne (scraping, dashboards, alertes) réduit la dépendance aux SaaS et crée un avantage compétitif. CAPEX stabilisé à ~400€/mois même à 15+ clients.',
              icon: Code2, iconColor: 'text-purple-500',
            },
            {
              title: 'Force commerciale variable',
              desc: 'Apporteurs d\'affaires à 10% de commission (sur les 6 premiers mois du CA client). Pas de coût fixe. Peut être un consultant hôtelier, un partenaire PMS, ou un commercial freelance.',
              icon: UserPlus, iconColor: 'text-orange-500',
            },
            {
              title: 'Alternant dès M3',
              desc: 'Un alternant SEO/Content à ~900€ brut/mois libère immédiatement 10-15h/mois du fondateur sur les tâches délégables. Coût très faible, formation intégrée.',
              icon: Star, iconColor: 'text-amber-500',
            },
            {
              title: 'Traduction IA (DeepL)',
              desc: 'DeepL Pro à 25€/mois remplace entièrement les traducteurs freelance pour les landing pages multilingues. Relecture humaine légère par l\'alternant.',
              icon: Languages, iconColor: 'text-pink-500',
            },
          ].map((point) => {
            const PIcon = point.icon;
            return (
              <div key={point.title} className="flex gap-3 p-3 bg-gray-50 rounded-lg">
                <PIcon className={`w-5 h-5 ${point.iconColor} flex-shrink-0 mt-0.5`} />
                <div>
                  <h3 className="text-xs font-bold text-gray-900">{point.title}</h3>
                  <p className="text-[11px] text-gray-500 mt-1 leading-relaxed">{point.desc}</p>
                </div>
              </div>
            );
          })}
        </div>
      </motion.div>

      {/* Résumé final */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.4 }}
        className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-xl p-6 text-white"
      >
        <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
          <TrendingUp className="w-5 h-5 text-stratos-gold" />
          Projection consolidée
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {[
            { label: 'Point mort', value: '3 clients', sub: '~6 300€ CA vs ~5 500€ charges', color: 'text-amber-400' },
            { label: 'CA Année 1', value: '~130K€', sub: '~8 clients fin Y1, mix 2 100€ moy.', color: 'text-emerald-400' },
            { label: 'CA Année 2', value: '~340K€', sub: '~17 clients fin Y2, +refonte/audits', color: 'text-emerald-400' },
            { label: 'EBITDA Y1', value: '~35-50K€', sub: '25-35% marge, startup mode', color: 'text-stratos-gold' },
            { label: 'EBITDA Y2', value: '~110-140K€', sub: '35-42% marge, équipe structurée', color: 'text-stratos-gold' },
          ].map((item) => (
            <div key={item.label} className="bg-white/5 rounded-lg p-3 border border-white/10">
              <div className={`text-xl font-bold ${item.color}`}>{item.value}</div>
              <div className="text-[11px] text-white font-medium mt-1">{item.label}</div>
              <div className="text-[10px] text-gray-400 mt-0.5">{item.sub}</div>
            </div>
          ))}
        </div>
        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-white/5 rounded-lg p-4 border border-white/10">
            <p className="text-stratos-gold font-medium mb-2 text-sm flex items-center gap-2">
              <ArrowRight className="w-4 h-4" />
              Le fondateur doit rester sur :
            </p>
            <ul className="space-y-1 text-xs text-gray-400">
              <li>• Simulations prompt IA & reverse-engineering (expertise unique)</li>
              <li>• Réunions client & closing (relation directe)</li>
              <li>• Architecture app & R&D (avantage compétitif)</li>
              <li>• Refontes sites clients (forte marge, expertise)</li>
            </ul>
          </div>
          <div className="bg-white/5 rounded-lg p-4 border border-white/10">
            <p className="text-emerald-400 font-medium mb-2 text-sm flex items-center gap-2">
              <ArrowRight className="w-4 h-4" />
              À déléguer immédiatement :
            </p>
            <ul className="space-y-1 text-xs text-gray-400">
              <li>• Animation GBP, réponses avis → alternant (M3)</li>
              <li>• Landing pages, contenu → junior SEO (M6)</li>
              <li>• Traductions → DeepL + relecture alternant</li>
              <li>• Reporting → templates automatisés + junior</li>
              <li>• Prospection → apporteurs d'affaires (commission)</li>
            </ul>
          </div>
        </div>
        <div className="mt-4 bg-white/5 rounded-lg p-4 border border-white/10">
          <p className="text-xs text-gray-400 leading-relaxed">
            <strong className="text-white">Note :</strong> Ce modèle est conservateur et adapté à une structure startup.
            La rémunération fondateur est volontairement basse en Y1. Les projections n'incluent pas les upsells (Signature),
            les formations one-shot, ni l'effet réseau (recommandations). Le churn B2B premium se situe à 15%/an
            avec des engagements 6-12 mois. La courbe d'apprentissage (-15%/an sur le temps/client) est documentée
            dans les agences SEO. Le passage de seuil se fait à <strong className="text-stratos-gold">3 clients</strong> (point mort)
            et l'embauche du 1er junior est nécessaire dès <strong className="text-stratos-gold">5 clients</strong>.
          </p>
        </div>
      </motion.div>
    </div>
  );
};
